import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000, // 5 minutes timeout (300000ms) for long-running queries
});

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log(`Making ${config.method?.toUpperCase()} request to ${config.url}`);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Detailed error logging for debugging
    console.error('API Error Details:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method,
      headers: error.response?.headers,
      request: error.request,
      code: error.code,
      isNetworkError: !error.response && error.request,
      isTimeout: error.code === 'ECONNABORTED' || error.message?.includes('timeout')
    });
    return Promise.reject(error);
  }
);

// Note: Connection/session endpoints are no longer supported by the backend

// Retry helper function
const retryRequest = async (requestFn, maxRetries = 2, delay = 1000) => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      // Don't retry on last attempt or if it's not a retryable error
      if (attempt === maxRetries) {
        throw error;
      }
      
      // Only retry on network errors or 5xx server errors
      const isRetryable = 
        !error.response || // Network error
        (error.response.status >= 500 && error.response.status < 600); // Server error
      
      if (!isRetryable) {
        throw error;
      }
      
      // Exponential backoff
      const waitTime = delay * Math.pow(2, attempt);
      console.log(`Request failed, retrying in ${waitTime}ms (attempt ${attempt + 1}/${maxRetries})...`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
  }
};

export const executeQueryStream = async (query, userId, onStatus) => {
  const response = await fetch(`${API_BASE_URL}/query-stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, user_id: userId }),
  });

  if (!response.ok) {
    throw new Error(`Server error (${response.status})`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalResult = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    let currentEvent = null;
    for (const line of lines) {
      if (line.startsWith('event: ')) {
        currentEvent = line.slice(7).trim();
      } else if (line.startsWith('data: ')) {
        try {
          const data = JSON.parse(line.slice(6));
          if (currentEvent === 'status' && onStatus) {
            onStatus(data.message, data.step);
          } else if (currentEvent === 'complete') {
            finalResult = data.data;
          }
        } catch (e) {
          console.warn('Failed to parse SSE data:', line);
        }
        currentEvent = null;
      }
    }
  }

  if (!finalResult) {
    throw new Error('No response received from server');
  }
  return finalResult;
};

export const executeQuery = async (query, userId) => {
  try {
    const response = await retryRequest(async () => {
      return await api.post('/query', {
        query,
        user_id: userId,
      });
    });
    return response.data;
  } catch (error) {
    // Enhanced error handling with detailed logging
    let errorMessage = 'Failed to execute query';
    
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const detail = error.response.data?.detail || error.response.data?.error || error.response.data?.message;
      
      console.error('Backend Error Response:', {
        status,
        detail,
        fullResponse: error.response.data,
        query: typeof query === 'string' ? query.substring(0, 100) : String(query).substring(0, 100) // Log first 100 chars of query
      });
      
      if (detail) {
        errorMessage = detail;
      } else if (status === 400) {
        errorMessage = 'Bad request. Please check your query and try again.';
      } else if (status === 401) {
        errorMessage = 'Unauthorized. Please check your credentials.';
      } else if (status === 404) {
        errorMessage = 'Endpoint not found. Please check the API configuration.';
      } else if (status === 500) {
        errorMessage = 'Server error occurred. Please try again later.';
      } else if (status === 503) {
        errorMessage = 'Service unavailable. The server may be overloaded.';
      } else {
        errorMessage = `Server error (${status}). ${detail || 'Please try again.'}`;
      }
    } else if (error.request) {
      // Request was made but no response received
      console.error('Network Error - No Response:', {
        message: error.message,
        code: error.code,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          timeout: error.config?.timeout
        }
      });
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Request timeout. The query is taking too long to process. Please try again or simplify your query.';
      } else if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (error.code === 'ECONNREFUSED') {
        errorMessage = 'Connection refused. The server may be down or unreachable.';
      } else {
        errorMessage = `Network error: ${error.message || 'Unable to reach the server'}`;
      }
    } else {
      // Error setting up the request
      console.error('Request Setup Error:', {
        message: error.message,
        stack: error.stack
      });
      errorMessage = `Request error: ${error.message || 'Failed to setup request'}`;
    }
    
    throw new Error(errorMessage);
  }
};

export const clearChatHistory = async (userId) => {
  try {
    if (!userId || !userId.trim()) {
      console.warn('Cannot clear chat history: user_id is required');
      return { success: false, message: 'User ID is required' };
    }
    
    const response = await api.post('/clear', {
      user_id: userId,
    });
    return response.data;
  } catch (error) {
    console.error('Error clearing chat history:', error);
    // Don't throw error, just log it - clearing is not critical
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to clear chat history',
      error: error.response?.data?.error || error.message
    };
  }
};

export default api;
